/**
 * Sprint 26.0 — full-page task detail mount (/tasks/:id).
 *
 * The SAME <TaskDetail> the pane renders, mounted full-page (two-column) for
 * deep-links, notifications, and mobile. Resolves the owning agent from the
 * task's own agent_id / agent_name so the breadcrumb + field edits have an
 * agent id.
 */

import { useParams, useNavigate, Navigate } from "react-router";
import { Breadcrumb, Card, EmptyState, type BreadcrumbSegment } from "../components/primitives";
import { useTaskDetail } from "../hooks/use-task-detail";
import { TaskDetail } from "../components/task-detail/TaskDetail";
import type { CSSProperties } from "react";

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useTaskDetail(id);

  if (!id) return <Navigate to="/home" replace />;

  if (query.isLoading) {
    return <div style={page}><div style={skeleton} /></div>;
  }
  if (query.error || !query.data) {
    return (
      <div style={page}>
        <Card>
          <EmptyState>{(query.error as Error)?.message ?? "Couldn't load this task."}</EmptyState>
        </Card>
      </div>
    );
  }

  const { task } = query.data;
  const agentId = task.agent_id ?? "";
  const agentName = task.agent_name ?? "Agent";

  const segments: BreadcrumbSegment[] = [];
  if (agentId) segments.push({ label: agentName, onClick: () => navigate(`/agents/${agentId}`) });
  segments.push({ label: task.title });

  return (
    <div style={page}>
      <div style={topBar}>
        <Breadcrumb segments={segments} />
      </div>
      <TaskDetail mount="page" agentId={agentId} agentName={agentName} taskId={task.id} />
    </div>
  );
}

const page: CSSProperties = {
  maxWidth: 980,
  margin: "0 auto",
  width: "100%",
  paddingTop: "2rem",
  paddingBottom: "2rem",
};

const topBar: CSSProperties = {
  paddingBottom: 14,
  borderBottom: "0.5px solid var(--color-border-tertiary)",
  marginBottom: 18,
};

const skeleton: CSSProperties = {
  height: 240,
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
};
